import { useMemo } from "react";
import {
  getBudgetStatus,
  type BudgetInsightLike,
  type BudgetStatus,
} from "@/domains/analytics/utils/budgetInsights";

export type CategoryInsight = BudgetInsightLike & {
  budgetId?: number;
  budgetDescription?: string | null;
};

type CategoryBalance = { id: number; name: string; category_total_expense?: number };
type BudgetBalance = { category_id?: number | null; id?: number; amount?: number; description?: string | null };

export const useCategoryInsights = (
  categories: CategoryBalance[],
  budgets: BudgetBalance[],
) => {
  const insights = useMemo<CategoryInsight[]>(() => {
    const budgetsByCategory = new Map(
      budgets
        .filter((budget) => typeof budget.category_id === "number")
        .map((budget) => [budget.category_id as number, budget]),
    );
    const totalExpense = categories.reduce(
      (sum, category) => sum + Number(category.category_total_expense ?? 0),
      0,
    );
    const statusRank: Record<BudgetStatus, number> = { over: 0, near: 1, safe: 2, none: 3 };

    return categories
      .map((category) => {
        const spent = Number(category.category_total_expense ?? 0);
        const budget = budgetsByCategory.get(category.id);
        const budgetAmount = Number(budget?.amount ?? 0);
        return {
          id: category.id,
          name: category.name,
          spent,
          percentage: totalExpense > 0 ? (spent / totalExpense) * 100 : 0,
          budgetId: budget?.id,
          budgetAmount: budgetAmount || undefined,
          budgetDescription: budget?.description ?? null,
          usagePercent: budgetAmount > 0 ? spent / budgetAmount : undefined,
          remaining: budgetAmount > 0 ? budgetAmount - spent : undefined,
          status: getBudgetStatus(spent, budgetAmount || undefined),
        };
      })
      .filter((category) => category.spent > 0)
      .sort((first, second) => {
        if (statusRank[first.status] !== statusRank[second.status]) {
          return statusRank[first.status] - statusRank[second.status];
        }
        if (first.status === "none" && second.status === "none") {
          return second.spent - first.spent;
        }
        return (second.usagePercent ?? -1) - (first.usagePercent ?? -1) || second.spent - first.spent;
      });
  }, [budgets, categories]);

  // Budget-pressure aggregates used to live here, ranked by overage on total spend. They
  // moved to buildDiscretionaryBudgetPressure, which ranks on what the user can actually cut.
  return { categoryInsights: insights };
};
